import { useCallback, useEffect, useRef, useState } from 'react';
import { T } from 'src/shared/helpers';
import {
    AgentApiError,
    createAgentMessageIdempotencyKey,
    createAgentConversation,
    getAgentConversation,
    getAgentDraft,
    postAgentMessage,
    replaceAgentSideImage,
    upsertAgentPrintArea,
    waitForAgentTurn
} from './agentClient';
import { AgentChatEntry, AgentDraft, AgentGesture, LocalAgentAttachment, ReplaceAgentSideImageInput, UpsertAgentPrintAreaInput } from './types';

const SESSION_STORAGE_KEY = 'zakeke.agent.conversationId';

const emptyDraft: AgentDraft = {
    draftId: null,
    revisionNumber: null,
    title: null,
    document: null
};

const entryId = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

interface RetryableAgentMessage {
    text: string;
    localAttachments: LocalAgentAttachment[];
    gesture: AgentGesture | null;
    idempotencyKey: string;
    conversationId: number | null;
    requestId: number | null;
}

const canRetryRequestError = (error: unknown) => {
    if (!(error instanceof AgentApiError)) return true;
    return ![400, 401, 403, 404, 422].includes(error.status);
};

export const messageForOutcome = (code: string) => {
    const messages: Record<string, string> = {
        max_iterations: T._(
            'The assistant paused after completing as many steps as this turn allows. Its progress was saved; send another message to continue.',
            'Admin'
        ),
        iteration_limit: T._(
            'The assistant paused after completing as many steps as this turn allows. Its progress was saved; send another message to continue.',
            'Admin'
        ),
        max_tool_calls: T._(
            'The assistant paused after completing as many operations as this turn allows. Its progress was saved; send another message to continue.',
            'Admin'
        ),
        tool_call_limit: T._(
            'The assistant paused after completing as many operations as this turn allows. Its progress was saved; send another message to continue.',
            'Admin'
        ),
        token_budget_exhausted: T._(
            'The assistant paused because this request was too large for one turn. Its progress was saved; continue with a shorter message.',
            'Admin'
        ),
        max_output_tokens: T._(
            'The assistant paused because its answer was too large for one turn. Its progress was saved; send another message to continue.',
            'Admin'
        ),
        token_limit: T._(
            'The assistant paused because this request was too large for one turn. Its progress was saved; continue with a shorter message.',
            'Admin'
        ),
        content_filtered: T._('The assistant could not complete this turn because the content was blocked. Nothing new was applied.', 'Admin'),
        no_terminal: T._('The assistant could not complete this turn. Nothing was applied.', 'Admin'),
        turn_in_progress: T._('The request is still being processed.', 'Admin')
    };

    return messages[code] ?? T._('The assistant could not complete the request.', 'Admin');
};

export const useAgentSession = (onExternalChanges?: () => void) => {
    const [conversationId, setConversationId] = useState<number | null>(null);
    const [entries, setEntries] = useState<AgentChatEntry[]>([]);
    const [draft, setDraft] = useState<AgentDraft>(emptyDraft);
    const [isBusy, setIsBusy] = useState(false);
    const [isDraftBusy, setIsDraftBusy] = useState(false);
    const [isRestoring, setIsRestoring] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [retryRequest, setRetryRequest] = useState<RetryableAgentMessage | null>(null);
    const requestControllerRef = useRef<AbortController | null>(null);
    const draftEditBusyRef = useRef(false);

    const refreshDraft = useCallback(async (id: number, signal?: AbortSignal) => {
        try {
            const currentDraft = await getAgentDraft(id, signal);
            setDraft(currentDraft ?? emptyDraft);
        } catch {
            // A preview failure must not turn a successful conversation turn into a failed one.
        }
    }, []);

    useEffect(() => {
        const restore = async () => {
            const storedId = Number(window.sessionStorage.getItem(SESSION_STORAGE_KEY));
            if (!Number.isFinite(storedId) || storedId <= 0) {
                setIsRestoring(false);
                return;
            }

            const controller = new AbortController();
            requestControllerRef.current = controller;

            try {
                const conversation = await getAgentConversation(storedId, controller.signal);
                if (!conversation) {
                    window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
                    return;
                }

                setConversationId(conversation.conversationId);
                setEntries(
                    conversation.turns.map((turn, index) => ({
                        id: `restored-${turn.turnId ?? index}`,
                        role: 'agent' as const,
                        turn
                    }))
                );
                await refreshDraft(conversation.conversationId, controller.signal);
            } catch (restoreError) {
                if ((restoreError as Error).name !== 'AbortError') window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
            } finally {
                setIsRestoring(false);
            }
        };

        void restore();
        return () => requestControllerRef.current?.abort();
    }, [refreshDraft]);

    const ensureConversation = useCallback(
        async (signal: AbortSignal) => {
            if (conversationId) return conversationId;

            const conversation = await createAgentConversation(`${T._('AI assistant', 'Admin')} - ${new Date().toLocaleString()}`, signal);
            setConversationId(conversation.conversationId);
            window.sessionStorage.setItem(SESSION_STORAGE_KEY, String(conversation.conversationId));
            return conversation.conversationId;
        },
        [conversationId]
    );

    const submitMessage = useCallback(
        async (request: RetryableAgentMessage, appendMerchantEntry: boolean) => {
            if (isBusy || draftEditBusyRef.current) return;

            setError(null);
            setRetryRequest(null);
            setIsBusy(true);
            requestControllerRef.current?.abort();
            const controller = new AbortController();
            requestControllerRef.current = controller;
            let retryCandidate = request;

            if (appendMerchantEntry) {
                setEntries((current) => [
                    ...current,
                    {
                        id: entryId(),
                        role: 'merchant',
                        text: request.text,
                        attachments: request.localAttachments
                    }
                ]);
            }

            try {
                const id = request.conversationId ?? (await ensureConversation(controller.signal));
                retryCandidate = { ...retryCandidate, conversationId: id };

                let requestId = request.requestId;
                if (!requestId) {
                    const queued = await postAgentMessage(
                        id,
                        request.text,
                        request.localAttachments.map(({ url, name, side, variant }) => ({ url, name, side, variant })),
                        request.gesture,
                        request.idempotencyKey,
                        controller.signal
                    );
                    requestId = queued.requestId;
                    retryCandidate = { ...retryCandidate, requestId };
                }

                const completed = await waitForAgentTurn(id, requestId, controller.signal);
                await refreshDraft(id, controller.signal);

                if (completed.state === 'completed' && completed.turn) {
                    if (completed.turn.hasExternalChanges) onExternalChanges?.();
                    setEntries((current) => [...current, { id: entryId(), role: 'agent', turn: completed.turn! }]);
                } else {
                    const code = completed.failureCode ?? 'no_terminal';
                    setEntries((current) => [...current, { id: entryId(), role: 'notice', code, message: messageForOutcome(code) }]);
                    setRetryRequest({
                        ...retryCandidate,
                        idempotencyKey: createAgentMessageIdempotencyKey(),
                        requestId: null
                    });
                }
            } catch (sendError) {
                if ((sendError as Error).name !== 'AbortError') {
                    const message = (sendError as Error).message || T._('The message could not be delivered.', 'Admin');
                    setError(message);
                    if (canRetryRequestError(sendError)) setRetryRequest(retryCandidate);
                }
            } finally {
                setIsBusy(false);
            }
        },
        [ensureConversation, isBusy, onExternalChanges, refreshDraft]
    );

    const send = useCallback(
        async (text: string, localAttachments: LocalAgentAttachment[] = [], gesture: AgentGesture | null = null) => {
            const trimmedText = text.trim();
            if (isBusy || draftEditBusyRef.current || (!trimmedText && localAttachments.length === 0 && !gesture)) return;

            await submitMessage(
                {
                    text: trimmedText,
                    localAttachments,
                    gesture,
                    idempotencyKey: createAgentMessageIdempotencyKey(),
                    conversationId,
                    requestId: null
                },
                true
            );
        },
        [conversationId, isBusy, submitMessage]
    );

    const retry = useCallback(async () => {
        if (!retryRequest) return;
        await submitMessage(retryRequest, false);
    }, [retryRequest, submitMessage]);

    const editDraft = useCallback(
        async (edit: (id: number, revisionNumber: number) => Promise<unknown>) => {
            if (!conversationId || !draft.revisionNumber || draftEditBusyRef.current) return false;

            setError(null);
            draftEditBusyRef.current = true;
            setIsDraftBusy(true);
            try {
                await edit(conversationId, draft.revisionNumber);
                await refreshDraft(conversationId);
                return true;
            } catch (draftError) {
                if (draftError instanceof AgentApiError && draftError.status === 409) {
                    await refreshDraft(conversationId);
                }
                setError((draftError as Error).message || T._('The draft could not be updated.', 'Admin'));
                return false;
            } finally {
                draftEditBusyRef.current = false;
                setIsDraftBusy(false);
            }
        },
        [conversationId, draft.revisionNumber, isDraftBusy, refreshDraft]
    );

    const upsertPrintArea = useCallback(
        (input: Omit<UpsertAgentPrintAreaInput, 'expectedRevisionNumber'>) =>
            editDraft((id, expectedRevisionNumber) => upsertAgentPrintArea(id, { ...input, expectedRevisionNumber })),
        [editDraft]
    );

    const replaceSideImage = useCallback(
        (input: Omit<ReplaceAgentSideImageInput, 'expectedRevisionNumber'>) =>
            editDraft((id, expectedRevisionNumber) => replaceAgentSideImage(id, { ...input, expectedRevisionNumber })),
        [editDraft]
    );

    const reset = useCallback(() => {
        requestControllerRef.current?.abort();
        window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
        setConversationId(null);
        setEntries([]);
        setDraft(emptyDraft);
        setError(null);
        setRetryRequest(null);
        setIsBusy(false);
        draftEditBusyRef.current = false;
        setIsDraftBusy(false);
    }, []);

    return {
        conversationId,
        entries,
        draft,
        isBusy,
        isDraftBusy,
        isRestoring,
        error,
        canRetry: retryRequest !== null,
        send,
        retry,
        upsertPrintArea,
        replaceSideImage,
        reset
    };
};
